/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Page for non-Showroom apps
 * @module Epic.AppOrchard.DownloadApp
 */

import {
	AbsoluteCenter,
	Box,
	Center,
	Heading,
	HStack,
	Stack,
	StackDivider,
	Text,
	useBreakpointValue,
	VStack,
} from "@chakra-ui/react";
import { ISuccessHandler, useAsync } from "@epic/react-async-hook";
import { showroomBorder, showroomGray, showroomText } from "ao/chakraTheme";
import { BodyContent } from "ao/components/Frame/BodyContainer";
import {
	ActionButtonsSection,
	AppBannerSection,
	AppHeaderDetails,
	AppInsightMetricsSection,
	DownloadStatusSection,
	ShowroomAboutThisAppSection,
} from "ao/components/Showroom/frameworkComponents/GalleryListingComponents/AppSections";
import { ScreenshotViewer } from "ao/components/Showroom/frameworkComponents/GeneralComponents/ScreenshotViewer";
import { ShowroomContactButton } from "ao/components/Showroom/frameworkComponents/GeneralComponents/ShowroomContactButton";
import { ShowroomErrorAlert } from "ao/components/Showroom/frameworkComponents/GeneralComponents/ShowroomErrorAlert";
import { Thumbnail } from "ao/components/Showroom/frameworkComponents/GeneralComponents/Thumbnail";
import { WebsiteButton } from "ao/components/Showroom/frameworkComponents/GeneralComponents/WebsiteButton";
import { useSiteInfoState } from "ao/state/siteInfo";
import { ITopLevelComponentProps } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { getIntFromSearchParam, isLocalhost } from "ao/utils/helpers";
import React, { FunctionComponent, memo, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getShowroomAppAdminUrl } from "routes/showroomListingAdmin";
import { AsyncPlaceholder, MainContentSpinner, ReturnLink, StatusAlert } from "../Core";
import { getDetails } from "./data/data";
import { NewerVersionBanner } from "./helperComponents/NewerVersionBanner";
import { AppLinksSection } from "./subsections/AppLinksSection";

const DownloadApp: FunctionComponent<ITopLevelComponentProps> = memo((props) => {
	const { userSecurity } = props;

	const [searchParams] = useSearchParams();
	const id = getIntFromSearchParam(searchParams, "id");

	const showroomRedirectMessagePrefix = "SHOWROOM_REDIRECT_";
	const navigate = useNavigate();
	const { siteInfo } = useSiteInfoState((selectors) => selectors.getState(), []);

	const [loaded, setLoaded] = useState<boolean>(false);
	const [loadError, setLoadError] = useState<string>("");

	const [appDetails, setAppDetails] = useState<IShowroomAppDownloadModel>();
	const appModel = appDetails?.appModel;

	const handleAppLoaded = useCallback<ISuccessHandler<typeof getDetails>>(
		(response) => {
			if (response.success) {
				setLoaded(true);
				setLoadError("");
				setAppDetails(response.model);
			} else {
				if (response.message.indexOf(showroomRedirectMessagePrefix) === 0) {
					const appId = response.message.split(showroomRedirectMessagePrefix)[1];

					if (isLocalhost() || siteInfo.isAdminSite) {
						navigate(getShowroomAppAdminUrl(appId), { replace: true });
					} else {
						window.location.href = `${siteInfo.showroomUrl}listing?appId=${appId}`;
					}
				}
				setLoadError(response.message);
			}
		},
		[navigate, siteInfo.isAdminSite, siteInfo.showroomUrl],
	);

	const [LoadingPlaceholder, getAppData] = useAsync(getDetails, AsyncPlaceholder, {
		displayName: "getAppDetails",
		onSuccess: handleAppLoaded,
		onFailure: () => setLoadError("App could not be loaded"),
	});

	useEffect(() => {
		getAppData(id);
	}, [getAppData, id]);

	const textColor = "black";
	const isMobile = useBreakpointValue({ base: true, lg: false }) || false;

	const canPurchase = userSecurity.isEpicCustomer && userSecurity.canPurchase;
	const isAppFromOwnOrg = appModel && userSecurity.userOrganizationId === appModel.organizationId;
	const canContact = canPurchase && !isAppFromOwnOrg && !appDetails?.isDownloaded;

	const subLogoText = appModel ? (
		<>
			<Heading
				as="h2"
				fontWeight="600"
				fontSize="2.5em"
				color={textColor}
				textAlign={isMobile ? "center" : undefined}
			>
				{appModel.name + " "}

				<Text
					display="contents"
					whiteSpace="nowrap"
					fontWeight="300"
					fontSize="1.5rem"
					fontStyle="italic"
					color={showroomText}
				>
					by {appModel.orgName}
				</Text>
			</Heading>
			{appModel.version && (
				<Heading
					fontWeight="300"
					fontSize="1.5rem"
					color={showroomText}
					textAlign={isMobile ? "center" : undefined}
				>
					Version {appModel.version}
				</Heading>
			)}
		</>
	) : (
		<></>
	);

	return (
		<BodyContent>
			<Box py="15px">
				{!userSecurity.isVendorUser && userSecurity.userOrganizationId !== null && (
					<ReturnLink defaultUrl="/Download" defaultText={"Downloads Page"} />
				)}
			</Box>

			{userSecurity.userOrganizationId === null ? (
				<Box>
					<Text fontWeight="400" fontSize="1.25rem">
						We couldn't find that listing.
					</Text>
					<Text fontWeight="400" fontSize="1rem" pt="5px">
						If you are an Epic community member looking to download an app that is not listed yet,
						log in to continue.
					</Text>
				</Box>
			) : (
				<LoadingPlaceholder inProgressIndicator={<MainContentSpinner fullPage />} forFullPage>
					{loaded && appDetails && appModel && (
						<>
							<NewerVersionBanner appDetails={appDetails} />

							{userSecurity.isEpicCustomer && (
								<AppBannerSection
									userSecurity={userSecurity}
									appDetails={appDetails}
									setAppDetails={setAppDetails}
									isShowroom={false}
								/>
							)}
							{(userSecurity.isEpicUser || isAppFromOwnOrg) &&
								appDetails.hasDownloadCustomerAllowlist && (
									<StatusAlert
										status="info"
										mb="2"
										message={
											userSecurity.isEpicUser &&
											appDetails.downloadCustomerAllowlistDisclaimer ? (
												<Box
													dangerouslySetInnerHTML={{
														__html:
															appDetails.downloadCustomerAllowlistDisclaimer,
													}}
												></Box>
											) : (
												"This app has an allowlist of Epic customers who can download it. Epic customers not on the allowlist cannot download the app."
											)
										}
									/>
								)}
							<Stack
								direction={isMobile ? "column" : "row"}
								spacing="30px"
								color={textColor}
								align={"unset"}
							>
								<Box>
									<Box
										minW={{ base: "unset", sm: "320px" }}
										minH={{ base: "unset", sm: "165px" }}
										border={showroomBorder}
										borderRadius="10px"
										background="transparent linear-gradient(160deg, #FFFFFF 0%, #FFFFFF66 100%)"
										overflow={appModel.website || canContact ? "visible" : "hidden"}
									>
										<Box pos="relative" m="auto" maxW="350px" maxH="210px">
											<Center
												aspectRatio="5 / 3"
												style={{ aspectRatio: "5 / 3" }}
												p="5px"
												background="#FFFFFF"
												borderRadius="0px"
												borderTopRadius="10px"
											>
												<Thumbnail
													showBackground
													src={appDetails.thumbnail?.imageData ?? ""}
													listingId={appModel.id}
													maxW={{ base: "310px", sm: "340px" }}
													maxH={{ base: "155px", sm: "200px" }}
													height="100%"
													objectFit="scale-down"
													alt={appModel.name}
												/>
											</Center>
										</Box>

										{(appModel.website || canContact) && (
											<HStack
												divider={<StackDivider borderColor={showroomGray} />}
												spacing="none"
												borderTop={showroomBorder}
												minH="10px"
											>
												{appModel.website && (
													<WebsiteButton
														appUrl={appModel.website}
														orgName={appModel.orgName}
														iconColor={textColor}
														buttonProps={cardButtonProps}
													/>
												)}

												{canContact && (
													<ShowroomContactButton
														appId={appModel.id}
														appName={appModel.name}
														orgName={appModel.orgName}
														iconColor={textColor}
														buttonProps={cardButtonProps}
													/>
												)}
											</HStack>
										)}
									</Box>
								</Box>
								{isMobile && <Box>{subLogoText}</Box>}

								<AppHeaderDetails
									userSecurity={userSecurity}
									appDetails={appDetails}
									setAppDetails={setAppDetails}
									reload={() => getAppData(id)}
									textColor={textColor}
									subLogoElements={subLogoText}
									onlyThumbnail={false}
									isShowroom={false}
								/>
							</Stack>

							<VStack my="30px" spacing="45px">
								{userSecurity.isEpicCustomer && (
									<DownloadStatusSection
										appDetails={appDetails}
										headerColor={showroomText}
									/>
								)}

								<ActionButtonsSection
									userSecurity={userSecurity}
									headerColor={showroomText}
									buttonBackgroundColor={"none"}
									buttonBorderColor={"none"}
									appDetails={appDetails}
									reload={() => getAppData(id)}
								/>

								{appModel.summary && (
									<Text
										w="100%"
										whiteSpace="pre-line"
										fontSize="1.5em"
										fontWeight="500"
										textAlign={isMobile ? "center" : undefined}
									>
										{appModel.summary}
									</Text>
								)}
								{appModel.description && (
									<Text
										w="100%"
										whiteSpace="pre-line"
										fontSize="1.2em"
										textAlign={isMobile ? "center" : undefined}
									>
										{appModel.description}
									</Text>
								)}

								{appDetails.disclaimer && (
									<Box
										textAlign="center"
										fontWeight="300"
										dangerouslySetInnerHTML={{
											__html: appDetails.disclaimer,
										}}
									/>
								)}

								<AppInsightMetricsSection listing={appDetails} textColor={textColor} />

								<ScreenshotViewer screenshots={appDetails.screenshots ?? []} />
								{appDetails.isDownloaded && (
									<AppLinksSection appDetails={appDetails} userSecurity={userSecurity} />
								)}

								<ShowroomAboutThisAppSection
									userSecurity={userSecurity}
									appDetails={appDetails}
									buttonBackgroundColor={"blue"}
									buttonBorderColor={"blue"}
									textColor={"black"}
									setAppDetails={setAppDetails}
								/>
							</VStack>
						</>
					)}
					{loadError && (
						<AbsoluteCenter>
							<ShowroomErrorAlert errorMessage={loadError} />
						</AbsoluteCenter>
					)}
				</LoadingPlaceholder>
			)}
		</BodyContent>
	);
});

export default DownloadApp;

const cardButtonProps = {
	fontSize: "1.1rem",
	width: "100%",
	height: "40px",
	borderRadius: "none",
};
